import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { 
  ArrowLeft,
  Library,
  Plus,
  Trash2,
  Utensils,
  Dumbbell,
  Users,
  Search,
  Copy,
  Check,
  Edit,
  Flame,
  Target,
  Zap
} from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminGoatLibrary = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  
  const [activeTab, setActiveTab] = useState("nutrition"); // nutrition | workout | goat
  const [loading, setLoading] = useState(true);
  
  // Plantillas
  const [nutritionTemplates, setNutritionTemplates] = useState([]);
  const [workoutTemplates, setWorkoutTemplates] = useState([]);
  
  // Filtros
  const [genderFilter, setGenderFilter] = useState("");
  const [objectiveFilter, setObjectiveFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modals
  const [showCreateNutrition, setShowCreateNutrition] = useState(false);
  const [newNutritionTemplate, setNewNutritionTemplate] = useState({
    name: "",
    gender: "hombre",
    objective: "recomposicion",
    calories: 2200,
    protein: 180,
    carbs: 220,
    fat: 70,
    meal_slots: [
      { name: "Desayuno", percent: 20 },
      { name: "Media Mañana", percent: 15 },
      { name: "Comida", percent: 30 },
      { name: "Merienda", percent: 15 },
      { name: "Cena", percent: 20 }
    ]
  });

  // Fetch plantillas
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const [nutRes, workoutRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/nutrition-templates`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/custom-programs/templates`, { headers: getAuthHeaders() })
      ]);
      
      setNutritionTemplates(nutRes.data.templates || []);
      setWorkoutTemplates(workoutRes.data.templates || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Error al cargar plantillas");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Crear plantilla nutricional
  const handleCreateNutritionTemplate = async () => {
    const totalPercent = newNutritionTemplate.meal_slots.reduce((sum, s) => sum + s.percent, 0);
    if (totalPercent !== 100) {
      toast.error(`Los porcentajes deben sumar 100%. Actual: ${totalPercent}%`);
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/api/admin/nutrition-templates`,
        newNutritionTemplate,
        { headers: getAuthHeaders() }
      );
      toast.success("Plantilla creada");
      setShowCreateNutrition(false);
      fetchTemplates();
    } catch (error) {
      toast.error("Error al crear plantilla");
    }
  };

  // Eliminar plantilla nutricional
  const handleDeleteNutritionTemplate = async (templateId) => {
    if (!window.confirm("¿Eliminar esta plantilla?")) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/admin/nutrition-templates/${templateId}`,
        { headers: getAuthHeaders() }
      );
      toast.success("Plantilla eliminada");
      fetchTemplates();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  // Filtrar plantillas
  const filteredNutrition = nutritionTemplates.filter(t => {
    if (genderFilter && t.gender !== genderFilter) return false;
    if (objectiveFilter && t.objective !== objectiveFilter) return false;
    if (searchQuery && !t.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const filteredWorkouts = workoutTemplates.filter(t => {
    if (searchQuery && !t.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-[#0B0B0B] p-4 pb-24">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin")}
          className="text-[#888] hover:text-white"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-xl font-heading font-bold text-white flex items-center gap-2">
            <Library className="w-6 h-6 text-[#D4AF37]" />
            Biblioteca GOAT
          </h1>
          <p className="text-[#888] text-sm">Plantillas de entrenamiento y nutrición</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <Button
          variant={activeTab === "nutrition" ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveTab("nutrition")}
          className={cn(
            activeTab === "nutrition" 
              ? "bg-[#D4AF37] text-[#0B0B0B]" 
              : "border-[#2A2A2A] text-white"
          )}
        >
          <Utensils className="w-4 h-4 mr-1" />
          Nutrición ({nutritionTemplates.length})
        </Button>
        <Button
          variant={activeTab === "workout" ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveTab("workout")}
          className={cn(
            activeTab === "workout" 
              ? "bg-[#D4AF37] text-[#0B0B0B]" 
              : "border-[#2A2A2A] text-white"
          )}
        >
          <Dumbbell className="w-4 h-4 mr-1" />
          Entrenos ({workoutTemplates.length})
        </Button>
      </div>

      {/* Search & Filters */}
      <div className="flex gap-2 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
          <Input
            placeholder="Buscar plantilla..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-[#1A1A1A] border-[#2A2A2A] text-white"
          />
        </div>
        {activeTab === "nutrition" && (
          <>
            <select
              value={genderFilter}
              onChange={(e) => setGenderFilter(e.target.value)}
              className="px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-white text-sm"
            >
              <option value="">Todos</option>
              <option value="hombre">Hombre</option>
              <option value="mujer">Mujer</option>
            </select>
            <select
              value={objectiveFilter}
              onChange={(e) => setObjectiveFilter(e.target.value)}
              className="px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-white text-sm"
            >
              <option value="">Todos</option>
              <option value="definicion">Definición</option>
              <option value="recomposicion">Recomposición</option>
              <option value="volumen">Volumen</option>
            </select>
          </>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className="h-24 bg-[#1A1A1A] rounded-lg animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {/* Nutrition Templates */}
          {activeTab === "nutrition" && (
            <div className="space-y-3">
              <Button
                onClick={() => setShowCreateNutrition(true)}
                className="w-full bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nueva Plantilla Nutricional
              </Button>
              
              {filteredNutrition.map((template) => (
                <Card key={template.id} className="bg-[#1A1A1A] border-[#2A2A2A]">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-white font-semibold">{template.name}</h3>
                        <div className="flex gap-2 mt-1">
                          <Badge className={cn(
                            "border-none text-xs",
                            template.gender === "hombre" ? "bg-blue-500/20 text-blue-400" : "bg-pink-500/20 text-pink-400"
                          )}>
                            {template.gender}
                          </Badge>
                          <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-none text-xs">
                            {template.objective}
                          </Badge>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteNutritionTemplate(template.id)}
                        className="text-[#666] hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-2 mt-3">
                      <div className="bg-[#0B0B0B] rounded p-2 text-center">
                        <Flame className="w-3 h-3 text-[#D4AF37] mx-auto mb-1" />
                        <p className="text-white font-bold text-sm">{template.daily_targets?.calories}</p>
                        <p className="text-[#666] text-[10px]">kcal</p>
                      </div>
                      <div className="bg-[#0B0B0B] rounded p-2 text-center">
                        <p className="text-red-400 font-bold text-sm">{template.daily_targets?.protein}g</p>
                        <p className="text-[#666] text-[10px]">Proteína</p>
                      </div>
                      <div className="bg-[#0B0B0B] rounded p-2 text-center">
                        <p className="text-yellow-400 font-bold text-sm">{template.daily_targets?.carbs}g</p>
                        <p className="text-[#666] text-[10px]">Carbos</p>
                      </div>
                      <div className="bg-[#0B0B0B] rounded p-2 text-center">
                        <p className="text-blue-400 font-bold text-sm">{template.daily_targets?.fat}g</p>
                        <p className="text-[#666] text-[10px]">Grasas</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 mt-3 text-xs text-[#666]">
                      <Utensils className="w-3 h-3" />
                      {Object.keys(template.meal_slots || {}).length} comidas
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {filteredNutrition.length === 0 && (
                <div className="text-center py-8 text-[#666]">
                  No hay plantillas que coincidan con los filtros
                </div>
              )}
            </div>
          )}

          {/* Workout Templates */}
          {activeTab === "workout" && (
            <div className="space-y-3">
              <Button
                onClick={() => navigate("/admin/custom-programs?new=template")}
                className="w-full bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nueva Plantilla de Entrenamiento
              </Button>
              
              {filteredWorkouts.map((template) => (
                <Card 
                  key={template.id} 
                  className="bg-[#1A1A1A] border-[#2A2A2A] cursor-pointer hover:border-[#D4AF37]/50"
                  onClick={() => navigate(`/admin/custom-programs/${template.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-white font-semibold">{template.name}</h3>
                        <div className="flex gap-2 mt-1">
                          <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-none text-xs">
                            {template.objective || "General"}
                          </Badge>
                          {template.days_per_week && (
                            <Badge className="bg-[#2A2A2A] text-[#888] border-none text-xs">
                              {template.days_per_week} días/sem
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Dumbbell className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    
                    {template.coach_notes && (
                      <p className="text-[#888] text-xs mt-2 line-clamp-2">{template.coach_notes}</p>
                    )}
                    
                    <div className="flex items-center gap-2 mt-3 text-xs text-[#666]">
                      <Zap className="w-3 h-3" />
                      {template.days_count || 0} días de entrenamiento
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {filteredWorkouts.length === 0 && (
                <div className="text-center py-8 text-[#666]">
                  <Dumbbell className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay plantillas de entrenamiento</p>
                  <p className="text-xs mt-1">Crea tu primera plantilla desde Rutinas Personalizadas</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Modal Crear Plantilla Nutricional */}
      <Dialog open={showCreateNutrition} onOpenChange={setShowCreateNutrition}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37]">Nueva Plantilla Nutricional</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-[#888] text-sm">Nombre</label>
              <Input
                value={newNutritionTemplate.name}
                onChange={(e) => setNewNutritionTemplate({...newNutritionTemplate, name: e.target.value})}
                placeholder="Ej: Hombre Definición 2200"
                className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[#888] text-sm">Género</label>
                <select
                  value={newNutritionTemplate.gender}
                  onChange={(e) => setNewNutritionTemplate({...newNutritionTemplate, gender: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-white"
                >
                  <option value="hombre">Hombre</option>
                  <option value="mujer">Mujer</option>
                </select>
              </div>
              <div>
                <label className="text-[#888] text-sm">Objetivo</label>
                <select
                  value={newNutritionTemplate.objective}
                  onChange={(e) => setNewNutritionTemplate({...newNutritionTemplate, objective: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-white"
                >
                  <option value="definicion">Definición</option>
                  <option value="recomposicion">Recomposición</option>
                  <option value="volumen">Volumen</option>
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[#888] text-sm">Calorías</label>
                <Input
                  type="number"
                  value={newNutritionTemplate.calories}
                  onChange={(e) => setNewNutritionTemplate({...newNutritionTemplate, calories: parseInt(e.target.value) || 0})}
                  className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white"
                />
              </div>
              <div>
                <label className="text-[#888] text-sm">Proteína (g)</label>
                <Input
                  type="number"
                  value={newNutritionTemplate.protein}
                  onChange={(e) => setNewNutritionTemplate({...newNutritionTemplate, protein: parseInt(e.target.value) || 0})}
                  className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white"
                />
              </div>
              <div>
                <label className="text-[#888] text-sm">Carbohidratos (g)</label>
                <Input
                  type="number"
                  value={newNutritionTemplate.carbs}
                  onChange={(e) => setNewNutritionTemplate({...newNutritionTemplate, carbs: parseInt(e.target.value) || 0})}
                  className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white"
                />
              </div>
              <div>
                <label className="text-[#888] text-sm">Grasas (g)</label>
                <Input
                  type="number"
                  value={newNutritionTemplate.fat}
                  onChange={(e) => setNewNutritionTemplate({...newNutritionTemplate, fat: parseInt(e.target.value) || 0})}
                  className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white"
                />
              </div>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowCreateNutrition(false)}
                className="flex-1 border-[#2A2A2A] text-white"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreateNutritionTemplate}
                className="flex-1 bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B]"
              >
                Crear Plantilla
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGoatLibrary;
